import React, { Component } from 'react';
import { Table, Spin, DatePicker, Card, Row, Col, Statistic, Tag, Input, Button } from 'antd';
import { fetchData } from '../../../../lib/apis';
import moment from 'moment';

const { RangePicker } = DatePicker;
const { Search } = Input;

export default class AILogsHistory extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            logs: [],
            total: 0,
            analytics: null,
            page: 1,
            pageSize: 20,
            dateRange: [moment().subtract(7, 'days'), moment()],
            searchText: ''
        };
    }

    componentDidMount() {
        this.loadLogs();
        this.loadAnalytics();
    }

    loadLogs = async () => {
        try {
            this.setState({ loading: true });
            const { page, pageSize } = this.state;
            
            const res = await fetchData({
                url: `api/v1/app/ai/logs`,
                data: {
                    company_id: this.props.companySelected?.company_id || 0,
                    limit: pageSize,
                    offset: (page - 1) * pageSize
                }
            });
            
            this.setState({
                logs: res.data || [],
                total: res.total || 0,
                loading: false
            });
        } catch (err) {
            this.setState({ loading: false });
            console.error('Error loading logs:', err);
        }
    };

    loadAnalytics = async () => {
        try {
            const { dateRange } = this.state;
            const [from, to] = dateRange;
            
            const res = await fetchData({
                url: `api/v1/app/ai/analytics`,
                data: {
                    company_id: this.props.companySelected?.company_id || 0,
                    from: from.format('YYYY-MM-DD'),
                    to: to.format('YYYY-MM-DD')
                }
            });
            
            this.setState({ analytics: res.data });
        } catch (err) {
            console.error('Error loading analytics:', err);
        }
    };

    handleDateChange = (dates) => {
        this.setState({ dateRange: dates }, () => {
            this.loadAnalytics();
        });
    };

    handlePageChange = (page, pageSize) => {
        this.setState({ page, pageSize }, () => {
            this.loadLogs();
        });
    };

    columns = [
        {
            title: 'Thời gian',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (val) => moment(val).format('DD/MM/YYYY HH:mm')
        },
        {
            title: 'Câu hỏi',
            dataIndex: 'question',
            key: 'question',
            ellipsis: true,
            width: 300
        },
        {
            title: 'Câu trả lời',
            dataIndex: 'answer',
            key: 'answer',
            ellipsis: true,
            width: 400
        },
        {
            title: 'Latency',
            dataIndex: 'latencyMs',
            key: 'latencyMs',
            width: 100,
            render: (val) => `${Number(val/1000).toFixed(2)}s`
        },
        {
            title: 'Rep by cache',
            dataIndex: 'cacheHit',
            key: 'cacheHit',
            width: 80,
            render: (val) => val ? 
                <Tag color="green">Có</Tag> : 
                <Tag color="blue">Không</Tag>
        },
        {
            title: 'Docs',
            dataIndex: 'documentsRetrieved',
            key: 'documentsRetrieved',
            width: 60
        },
        {
            title: 'Status',
            dataIndex: 'error',
            key: 'error',
            width: 80,
            render: (val) => val ? 
                <Tag color="red">Error</Tag> : 
                <Tag color="green">OK</Tag>
        }
    ];

    render() {
        const { loading, logs, total, analytics, page, pageSize, dateRange } = this.state;

        return (
            <div style={{ padding: 20 }}>
                {/* Analytics Cards */}
                <Row gutter={16} style={{ marginBottom: 20 }}>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Tổng queries"
                                value={analytics?.totalQueries || 0}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Cache Hit Rate"
                                value={analytics?.cacheHitRate || 0}
                                suffix="%"
                                precision={1}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Avg Latency"
                                value={analytics?.avgLatencyMs || 0}
                                suffix="ms"
                                precision={0}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Error Rate"
                                value={analytics?.errorRate || 0}
                                suffix="%"
                                precision={2}
                                valueStyle={{ color: (analytics?.errorRate || 0) > 5 ? '#cf1322' : '#3f8600' }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Filters */}
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={8}>
                        <RangePicker
                            value={dateRange}
                            onChange={this.handleDateChange}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col span={4}>
                        <Button type="primary" onClick={() => { this.loadLogs(); this.loadAnalytics(); }}>
                            Refresh
                        </Button>
                    </Col>
                </Row>

                {/* Top Questions */}
                {analytics?.topQuestions?.length > 0 && (
                    <Card title="Câu hỏi phổ biến" style={{ marginBottom: 16 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {analytics.topQuestions.slice(0, 5).map((q, i) => (
                                <Tag key={i} style={{ padding: '4px 8px' }}>
                                    {q.question.slice(0, 50)}... ({q.count})
                                </Tag>
                            ))}
                        </div>
                    </Card>
                )}

                {/* Logs Table */}
                <Spin spinning={loading}>
                    <Table
                        columns={this.columns}
                        dataSource={logs}
                        rowKey="id"
                        pagination={{
                            current: page,
                            pageSize: pageSize,
                            total: total,
                            onChange: this.handlePageChange,
                            showSizeChanger: true,
                            showTotal: (total) => `Tổng ${total} logs`
                        }}
                        scroll={{ x: 1200 }}
                        size="small"
                    />
                </Spin>
            </div>
        );
    }
}
